import { supabase } from '../lib/supabase';
import {
  getSubscriberOrderColumn,
  getSubscriberSignedAt
} from '../utils/subscriberFields';

async function rpcNewsletterIsActive(normalizedEmail) {
  const { data, error } = await supabase.rpc('newsletter_is_active', {
    p_email: normalizedEmail
  });
  if (error) return { ok: false, error };
  return { ok: true, value: !!data };
}

/**
 * Check if an email is already subscribed
 * @param {string} email - Email address to check
 * @returns {Promise<boolean>} - True if email exists and is active
 */
export const checkEmailExists = async (email) => {
  try {
    const normalizedEmail = email.toLowerCase().trim();
    const rpc = await rpcNewsletterIsActive(normalizedEmail);
    if (rpc.ok) return rpc.value;

    const { data, error } = await supabase
      .from('subscribers')
      .select('email, is_active')
      .eq('email', normalizedEmail)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error checking email:', error);
      return false;
    }

    return data && data.is_active;
  } catch (error) {
    console.error('Error checking email:', error);
    return false;
  }
};

/**
 * Subscribe a new email address
 * @param {string} email - Email address to subscribe
 * @param {string} source - Source of subscription (default: 'notify_modal')
 * @returns {Promise<{success: boolean, message: string, data?: any}>}
 */
export const subscribeEmail = async (email, source = 'notify_modal') => {
  try {
    const normalizedEmail = email.toLowerCase().trim();
    
    // Validate email format
    if (!/\S+@\S+\.\S+/.test(normalizedEmail)) {
      return {
        success: false,
        message: 'Please enter a valid email address'
      };
    }

    // Check if email already exists
    const exists = await checkEmailExists(normalizedEmail);
    if (exists) {
      return {
        success: false,
        message: 'This email is already subscribed'
      };
    }

    const rpcResult = await supabase.rpc('newsletter_subscribe', {
      p_email: normalizedEmail,
      p_source: source
    });

    if (!rpcResult.error && rpcResult.data != null) {
      let payload = rpcResult.data;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch {
          payload = null;
        }
      }
      if (payload && typeof payload === 'object') {
        return {
          success: !!payload.success,
          message:
            payload.message ||
            (payload.success ? 'Successfully subscribed!' : 'Failed to subscribe.'),
          data: payload.data
        };
      }
    }

    const { data, error } = await supabase
      .from('subscribers')
      .insert([
        {
          email: normalizedEmail,
          source: source,
          is_active: true
        }
      ])
      .select();

    if (error) {
      console.error('Error subscribing email:', error);
      return {
        success: false,
        message: 'Failed to subscribe. Please try again.'
      };
    }

    return {
      success: true,
      message: 'Successfully subscribed!',
      data: data[0]
    };
  } catch (error) {
    console.error('Error subscribing email:', error);
    return {
      success: false,
      message: 'Something went wrong. Please try again.'
    };
  }
};

/**
 * Unsubscribe an email address
 * @param {string} email - Email address to unsubscribe
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const unsubscribeEmail = async (email) => {
  try {
    const normalizedEmail = email.toLowerCase().trim();

    const rpcResult = await supabase.rpc('newsletter_unsubscribe', {
      p_email: normalizedEmail
    });

    if (!rpcResult.error && rpcResult.data != null) {
      let payload = rpcResult.data;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch {
          payload = null;
        }
      }
      if (payload && typeof payload === 'object') {
        return {
          success: !!payload.success,
          message: payload.message || ''
        };
      }
    }

    const { data, error } = await supabase
      .from('subscribers')
      .update({ is_active: false })
      .eq('email', normalizedEmail)
      .select();

    if (error) {
      console.error('Error unsubscribing email:', error);
      return {
        success: false,
        message: 'Failed to unsubscribe. Please try again.'
      };
    }

    if (!data || data.length === 0) {
      return {
        success: false,
        message: 'Email not found in our records'
      };
    }

    return {
      success: true,
      message: 'Successfully unsubscribed!'
    };
  } catch (error) {
    console.error('Error unsubscribing email:', error);
    return {
      success: false,
      message: 'Something went wrong. Please try again.'
    };
  }
};

/**
 * Get all active subscribers (for admin use)
 * @param {number} limit - Number of records to return
 * @param {number} offset - Number of records to skip
 * @returns {Promise<{success: boolean, data?: any[], error?: string}>}
 */
export const getActiveSubscribers = async (limit = 100, offset = 0) => {
  try {
    const { data, error } = await supabase
      .from('subscribers')
      .select('id, email, source, created_at')
      .eq('is_active', true)
      .order(getSubscriberOrderColumn(), { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('Error fetching subscribers:', error);
      return {
        success: false,
        error: 'Failed to fetch subscribers'
      };
    }

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    console.error('Error fetching subscribers:', error);
    return {
      success: false,
      error: 'Something went wrong while fetching subscribers'
    };
  }
};

/**
 * Get subscription statistics
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const getSubscriptionStats = async () => {
  try {
    const { data, error } = await supabase
      .from('subscribers')
      .select('is_active, source, created_at');

    if (error) {
      console.error('Error fetching subscription stats:', error);
      return {
        success: false,
        error: 'Failed to fetch subscription statistics'
      };
    }

    const stats = {
      total: data.length,
      active: data.filter(sub => sub.is_active).length,
      inactive: data.filter(sub => !sub.is_active).length,
      sources: {},
      recent: data
        .filter(sub => sub.is_active)
        .sort(
          (a, b) =>
            new Date(getSubscriberSignedAt(b) || 0) -
            new Date(getSubscriberSignedAt(a) || 0)
        )
        .slice(0, 10)
    };

    // Count by source
    data.forEach(sub => {
      stats.sources[sub.source] = (stats.sources[sub.source] || 0) + 1;
    });

    return {
      success: true,
      data: stats
    };
  } catch (error) {
    console.error('Error fetching subscription stats:', error);
    return {
      success: false,
      error: 'Something went wrong while fetching statistics'
    };
  }
};
